import { useEffect, useState } from 'react'
import { getUnreadCount } from '../api/messages'
import { useMessages } from './useMessages'

/*
 * 🔴 READING MOVES THE NUMBER TOO, AND NO SOCKET FRAME SAYS SO.
 *
 * The server marks a thread read when it is opened (`GET /players/:id/messages`),
 * but the badge only refetched on an arriving frame — so "1 unread" stayed on the
 * nav after the parent had opened the thread and even replied, until a reload.
 * The thread screen calls `refreshUnreadMessages` once its GET has landed, and
 * every mounted badge asks the server again. A module-level signal rather than a
 * context: the badge lives in the router's shell and the thread several screens
 * below it, and nothing between them should have to carry a callback.
 */
const readListeners = new Set<() => void>()

/** Subscribe to "a thread was read". Returns the unsubscribe. */
export function onUnreadRefresh(listener: () => void): () => void {
  readListeners.add(listener)
  return () => {
    readListeners.delete(listener)
  }
}

/** Ask every mounted unread badge to refetch — call after a thread has been opened (and so marked read). */
export function refreshUnreadMessages(): void {
  for (const l of readListeners) l()
}

/**
 * How many messages are waiting, for the badge on a nav item.
 *
 * 🔴 `getUnreadCount` HAS EXISTED AND HAD NO CALLER. `GET /parent/messages/unread`
 * is built, scoped and tested, and nothing in the app has ever asked it — the
 * same shape as `ChildSwitcher`, which fetched its list and drew nothing. A
 * parent could only discover a message by opening the Messages screen and
 * looking, which is the one thing a badge exists to save.
 *
 * 🔴 THE SOCKET MOVES THE NUMBER, NOT A TIMER. A poll would be a request every
 * few seconds for a value that changes a handful of times a day, and would still
 * be up to one interval stale. `useMessages` already holds the live frames this
 * tab has seen; when one arrives the count is refetched once, so the badge is
 * correct rather than incremented-and-hoped.
 *
 * ⚠️ IT REFETCHES RATHER THAN ADDING ONE. The server's count is the truth —
 * it knows about messages this tab never saw (another device, or before the tab
 * opened) and it knows what has been marked read. A local `+1` drifts from it
 * the first time either happens, and a badge nobody trusts is worse than none.
 *
 * ⚠️ NO TOKEN, NO REQUEST. A signed-out shell must not ask, and the hook is
 * called unconditionally from the shell — hooks cannot be conditional — so
 * `enabled` is the switch rather than the call site.
 */
export function useUnreadMessages(token: string | undefined, enabled: boolean): number {
  const [unread, setUnread] = useState(0)
  const live = useMessages(enabled ? token : undefined)

  /*
   * ⚠️ KEYED ON THE FRAME COUNT, NOT THE ARRAY. `useMessages` returns a new
   * array identity on every frame it appends, and depending on the array itself
   * would refetch on every render.
   */
  const seen = live.length
  /* Bumped by `refreshUnreadMessages` — a thread was opened and marked read. */
  const [reads, setReads] = useState(0)
  useEffect(
    () =>
      onUnreadRefresh(() => {
        setReads((n) => n + 1)
      }),
    [],
  )

  useEffect(() => {
    if (!enabled || token === undefined || token === '') {
      setUnread(0)
      return undefined
    }

    /*
     * ⚠️ AN ABORT, BECAUSE THIS REFIRES ON EVERY ARRIVAL. Two messages landing
     * together start two requests, and without this the slower one can resolve
     * last and write a staler number over a fresher one.
     */
    const abort = new AbortController()
    void getUnreadCount({ token, signal: abort.signal })
      .then((r) => {
        setUnread(r.unread)
      })
      .catch(() => {
        /*
         * ⚠️ SILENT, AND THE BADGE SIMPLY DOES NOT MOVE. This is decoration on a
         * nav item: a failed count must not raise anything a reader has to
         * dismiss, and the Messages screen itself reports its own failures.
         */
      })

    return () => {
      abort.abort()
    }
  }, [token, enabled, seen, reads])

  return unread
}
